import useDeliveryMethodsHook from "@/shared/hooks/useDeliveryMethodsHook";
import { renderToStaticMarkup } from "react-dom/server";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Question from "../Base/Question";
import Boolean from "../Boolean/BooleanWithLabel";
import Button from "../Button/Button";
import Textfield from "../Input/Textfield";
import { DeliveryMethods } from "@/shared/utils/enum";
import useThuisbezorgdQuery from "@/shared/queries/thuisbezorgd/useThuisbezorgdQuery";
import useThuisbezorgdOpenCloseMutation from "@/shared/mutations/thuisbezorgd/useThuisbezorgdOpenCloseMutation";
import Icon from "../Icon/Icon";
import Tooltip from "@/Components/Tooltip/Tooltip";

const HelperNoodremForm = () => {
  const {
    handleSubmit,
    register,
    control,
    setValue,
    errors,
    getValues,
    reset,
  } = useForm();
  const { t } = useTranslation("");
  const { inuseDeliveryMethodsQuery, updateDeliveryMethods } =
    useDeliveryMethodsHook();
  const { data: thuisbezorgdData } = useThuisbezorgdQuery();
  const thuisbezorgdOpenCloseMutation = useThuisbezorgdOpenCloseMutation();

  const takeawayData = inuseDeliveryMethodsQuery?.data?.find(
    (i) => i.delivery_method_id === DeliveryMethods.TAKEAWAY
  );
  const deliveryData = inuseDeliveryMethodsQuery?.data?.find(
    (i) => i.delivery_method_id === DeliveryMethods.DELIVERY
  );

  const isTakeawayActive = useWatch({
    control,
    name: "takeaway.is_active",
    defaultValue: takeawayData?.is_active ?? false,
  });

  const isTakeawayEmergency = useWatch({
    control,
    name: "takeaway.emergency_break_active",
    defaultValue: takeawayData?.emergency_break_active ?? false,
  });

  const isDeliveryActive = useWatch({
    control,
    name: "delivery.is_active",
    defaultValue: deliveryData?.is_active ?? false,
  });

  const isDeliveryEmergency = useWatch({
    control,
    name: "delivery.emergency_break_active",
    defaultValue: deliveryData?.emergency_break_active ?? false,
  });

  const onSubmit = (data) => {
    if (takeawayData) {
      const takeawaySubmitData = {
        emergency_break_minutes: 0,
        ...takeawayData,
        in_use: undefined,
        ...data.takeaway,
      };

      if (!takeawaySubmitData.emergency_break_active) {
        takeawaySubmitData.emergency_break_active = false;
      }

      updateDeliveryMethods
        .mutateAsync([takeawaySubmitData])
        .then(() => reset({ ...getValues(), takeaway: takeawaySubmitData }));
    }

    if (deliveryData) {
      const deliverySubmitData = {
        emergency_break_minutes: 0,
        ...deliveryData,
        in_use: undefined,
        ...data.delivery,
      };

      if (!deliverySubmitData.emergency_break_active) {
        deliverySubmitData.emergency_break_active = false;
      }

      updateDeliveryMethods
        .mutateAsync([deliverySubmitData])
        .then(() => reset({ ...getValues(), delivery: deliverySubmitData }));
    }
  };

  const handleThuisbezorgdOpenClose = (openOrClose: "open" | "close") => {
    thuisbezorgdOpenCloseMutation
      .mutateAsync({
        restaurant: thuisbezorgdData.restaurant_id,
        open_or_close: openOrClose,
      })
      .then(() => {
        const takeawayData = getValues().takeaway ?? {};
        const deliveryData = getValues().delivery ?? {};

        if (!takeawayData.emergency_break_active) {
          takeawayData.emergency_break_active = false;
        }

        if (!deliveryData.emergency_break_active) {
          deliveryData.emergency_break_active = false;
        }

        if (openOrClose === "open") {
          onSubmit({
            takeaway: { ...(getValues().takeaway ?? {}), is_active: true },
            delivery: { ...(getValues().delivery ?? {}), is_active: true },
          });
        }
      });
  };

  const hasDelivery = inuseDeliveryMethodsQuery?.data
    ?.map((item) => item.delivery_method_id)
    ?.includes(DeliveryMethods.DELIVERY);
  const hasTakeaway = inuseDeliveryMethodsQuery?.data
    ?.map((item) => item.delivery_method_id)
    ?.includes(DeliveryMethods.TAKEAWAY);

  return (
    <div className="w-full p-[2rem]">
      <Tooltip
        content={
          <div
            dangerouslySetInnerHTML={{
              __html: `${renderToStaticMarkup(
                <div className="max-w-[90vw] md:max-w-[55rem]">
                  {t("whatIsEmergencyBreakDescription")}
                </div>
              )}`,
            }}
          />
        }
      >
        <div>
          <Question display="inline-block">
            <h3 className="inline mt-[0]">{t("whatIsEmergencyBreak")}</h3>
          </Question>
        </div>
      </Tooltip>

      <form onSubmit={handleSubmit(onSubmit)}>
        {hasTakeaway && (
          <div className="mb-[2rem]">
            <div className="flex gap-4">
              <Controller
                name="takeaway.is_active"
                control={control}
                defaultValue={takeawayData.is_active}
                render={(props) => (
                  <Boolean
                    checked={!props.value}
                    onChange={(e) => {
                      props.onChange(!e.target.checked);
                      setValue("isTakeawayEmergency", false);
                    }}
                  >
                    <h3>{t("disableTakeawayTemporary")}</h3>
                  </Boolean>
                )}
              />
            </div>

            <div className="flex flex-col gap-4">
              {!isTakeawayActive && (
                <Textfield
                  name="takeaway.is_not_active_description"
                  defaultValue={takeawayData.is_not_active_description}
                  placeholder={t("emergencyBreakPlaceholder")}
                  ref={register({})}
                />
              )}
              {isTakeawayActive && (
                <>
                  <Controller
                    name="takeaway.emergency_break_active"
                    control={control}
                    defaultValue={takeawayData.emergency_break_active}
                    render={(props) => (
                      <Boolean
                        checked={props.value || false}
                        onChange={(e) => props.onChange(e.target.checked)}
                      >
                        <Tooltip
                          content={
                            <div
                              dangerouslySetInnerHTML={{
                                __html: `${renderToStaticMarkup(
                                  <div className="max-w-[90vw] md:max-w-[55rem]">
                                    {t("emergencyBreakInfo")}
                                  </div>
                                )}`,
                              }}
                            />
                          }
                        >
                          <div>
                            <Question>
                              <h3>{t("orders_live.takeawayEmergency")}</h3>
                            </Question>
                          </div>
                        </Tooltip>
                      </Boolean>
                    )}
                  />

                  {isTakeawayEmergency && (
                    <>
                      <Textfield
                        name="takeaway.emergency_break_message"
                        placeholder={t("emergencyBreakPlaceholder")}
                        defaultValue={takeawayData.emergency_break_message}
                        ref={register({})}
                      />

                      <Textfield
                        name="takeaway.emergency_break_minutes"
                        error={
                          errors?.takeaway?.emergency_break_minutes?.message
                        }
                        type="number"
                        placeholder={t("emergencyBreakMinPlaceholder")}
                        defaultValue={takeawayData.emergency_break_minutes}
                        ref={register({
                          required: {
                            value: true,
                            message: t("fieldRequired"),
                          },
                        })}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {hasDelivery && (
          <div className="mb-[2rem] border-t-[1px_solid_gray]">
            <div className="flex gap-4">
              <Controller
                name="delivery.is_active"
                control={control}
                defaultValue={deliveryData.is_active}
                render={(props) => (
                  <Boolean
                    checked={!props.value}
                    onChange={(e) => {
                      props.onChange(!e.target.checked);
                      setValue("isDeliveryEmergency", false);
                    }}
                  >
                    <h3>{t("disableDeliveryTemporary")}</h3>
                  </Boolean>
                )}
              />
            </div>
            <div className="flex flex-col gap-4">
              {!isDeliveryActive && (
                <Textfield
                  name="delivery.is_not_active_description"
                  placeholder={t("orders_live.deliveryInactiveMessage")}
                  defaultValue={deliveryData.is_not_active_description}
                  ref={register({})}
                />
              )}
              {isDeliveryActive && (
                <>
                  <Controller
                    name="delivery.emergency_break_active"
                    control={control}
                    defaultValue={deliveryData.emergency_break_active}
                    render={(props) => (
                      <Boolean
                        checked={props.value || false}
                        onChange={(e) => props.onChange(e.target.checked)}
                      >
                        <Tooltip
                          content={
                            <div
                              dangerouslySetInnerHTML={{
                                __html: `${renderToStaticMarkup(
                                  <div className="max-w-[90vw] md:max-w-[55rem]">
                                    {t("emergencyBreakInfo")}
                                  </div>
                                )}`,
                              }}
                            />
                          }
                        >
                          <div>
                            <Question>
                              <h3>{t("orders_live.deliveryEmergency")}</h3>
                            </Question>
                          </div>
                        </Tooltip>
                      </Boolean>
                    )}
                  />

                  {isDeliveryEmergency && (
                    <>
                      <Textfield
                        name="delivery.emergency_break_message"
                        placeholder={t("emergencyBreakPlaceholder")}
                        defaultValue={deliveryData.emergency_break_message}
                        ref={register({})}
                      />

                      <Textfield
                        name="delivery.emergency_break_minutes"
                        error={
                          errors?.delivery?.emergency_break_minutes?.message
                        }
                        type="number"
                        placeholder={t("emergencyBreakMinPlaceholder")}
                        defaultValue={deliveryData.emergency_break_minutes}
                        ref={register({
                          required: {
                            value: true,
                            message: t("fieldRequired"),
                          },
                        })}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        <Button isPending={updateDeliveryMethods.isPending}>{t("save")}</Button>
      </form>

      {thuisbezorgdData?.restaurant_id && (
        <div className="mt-[3rem] pt-[2rem] border-t-[2px_solid] border-[gray]">
          <div className="mb-6 flex items-center gap-4">
            <Icon className="h-12 w-12" variants="thuisbezorgd" />
            <div className="text-[2rem]" style={{ fontWeight: "bold" }}>
              {t("Thuisbezorgd_nl")}
            </div>
          </div>
          <div className="grid grid-cols-[1fr_1fr] gap-[1rem]">
            <Button
              onClick={() => handleThuisbezorgdOpenClose("open")}
              isPending={thuisbezorgdOpenCloseMutation.isPending}
            >
              {t("open")}
            </Button>
            <Button
              variant="danger"
              onClick={() => handleThuisbezorgdOpenClose("close")}
              isPending={thuisbezorgdOpenCloseMutation.isPending}
            >
              {t("close")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelperNoodremForm;
